import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Activity, Check, CheckCircle2, ChevronDown, ChevronRight, CircleAlert, Clock,
  CloudUpload, FileCheck2, FileUp, FlaskConical, Inbox, Loader2,
  Package, TestTube2, Timer, UserRound, X,
} from 'lucide-react';

import {
  assembleXomicsFile,
  completeXomicsUpload,
  createXomicsUpload,
  findCollectionByCode,
  loadSample,
  loadNextVisit,
  loadRequestedUploads,
  loadUploadCatalogue,
  loadXomicsJobs,
  loadXomicsUpload,
  loadXomicsUploads,
  lookupReportPatient,
  putSignedFile,
  XomicsApiError,
} from './api';
import { Field, Pager, errorMessage, fieldAria } from './ui';
import {
  Head, RailHeader, SearchField, WorkspaceTabs, duration,
} from './parts';
import {
  missingFactsMessage, missingPatientFacts, requiredPatientFacts, usePatientLookup,
} from './usePatientLookup';
import { useLiveTopics } from './useLive';
import { useXomicsAuth } from './AuthContext';
import { tx } from './i18n';
import { codeLabel, statusLabel } from './labels';

const PAGE_SIZE = 50;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const EMPTY_JOB_COUNTS = Object.freeze({
  all: 0, queued: 0, running: 0, completed: 0, failed: 0,
});

// The three-step shape of an upload is deliberate and visible here: the browser
// asks for signed URLs, PUTs the bytes straight to object storage, then asks for
// them to be verified. Nothing becomes a job until that last step succeeds.
const STEPS = { idle: 'idle', creating: 'creating', uploading: 'uploading', verifying: 'verifying' };

function bytes(value) {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  return `${(size / (1024 ** index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function when(value) {
  return value ? new Date(Number(value)).toLocaleString() : '—';
}

function positiveId(value) {
  if (value === null || !/^[1-9][0-9]*$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function hasFiles(value) {
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
}

// What is typed or scanned for an upload, as it is looked up: a collection's
// code, with or without its product's letters, or a tube's code. How long a
// code is belongs to the generator; nothing is judged here.
export function collectionLookupCode(value) {
  const code = value.toUpperCase().replace(/\s+/g, '');
  return code === '' ? null : code;
}

// The pipelines an upload from a collection can run. A tube that went through
// lab workflows has data for the pipelines those workflows feed, and the ones
// the collection was made for start ticked. With no lab work on record, a
// collection made for pipelines offers those; a tube made without any offers
// every pipeline that takes its sample type (`offered` is null).
export function collectionUploadPipelines(sample, labWorkflows, tubeCode) {
  const own = sample.pipelines.filter(p => p.available).map(p => p.id);
  const worked = labWorkflows.filter(order => tubeCode === null || order.tubeCode === tubeCode);
  if (worked.length === 0) return { offered: sample.pipelines.length === 0 ? null : own, own, workflows: [] };
  return { offered: [...new Set(worked.flatMap(order => order.pipelineIds))], own, workflows: [...new Set(worked.map(order => order.workflow.code))] };
}

// The same, as the pipelines of the catalogue.
export function offeredPipelines(catalogue, pipelines, sample) {
  return catalogue.pipelines.filter(p => pipelines.offered === null ? p.sample_codes.includes(sample.sampleType.code) : pipelines.offered.includes(p.id));
}

// Where the form starts for a collection: its own sample type where an offered
// pipeline takes it, otherwise the type the offered pipelines take, with the
// collection's own pipelines ticked. A single pipeline to choose from is ticked.
// `all` ticks every offered one: a customer's sample runs every pipeline of the
// workflow they ordered.
export function collectionUploadStart(sample, pipelines, catalogue, { all = false } = {}) {
  const offered = offeredPipelines(catalogue, pipelines, sample);
  if (offered.length === 0) return { sampleCode: sample.sampleType.code, selected: pipelines.own };
  const sampleCode = offered.some(p => p.sample_codes.includes(sample.sampleType.code)) ? sample.sampleType.code : offered[0].sample_codes[0];
  return { sampleCode, selected: collectionUploadSelection(pipelines, offered, sampleCode, { all }) };
}

// What is ticked for a sample type of a collection: every offered pipeline taking
// it (`all`), or the collection's own, or the one there is.
export function collectionUploadSelection(pipelines, offered, sampleCode, { all = false } = {}) {
  const choices = offered.filter(p => p.sample_codes.includes(sampleCode));
  if (all) return choices.map(p => p.id);
  const own = pipelines.own.filter(id => choices.some(p => p.id === id));
  return own.length === 0 && choices.length === 1 ? [choices[0].id] : own;
}

function StateChip({ status }) {
  return <span className={`xomics-chip is-${status}`}>{statusLabel(status)}</span>;
}

function CollectionLink({ collection, className = '' }) {
  if (!collection?.uuid) return null;
  const code = collection.printedCode ?? collection.code;
  const productName = collection.product === null ? collection.template.name : collection.product.name;
  const imageUrl = collection.product?.mainImageThumbnailUrl || null;
  return (
    <Link className={`xo-upload-kit-link${className ? ` ${className}` : ''}`}
      to={`/xomics/sample-tracking?sampleUuid=${encodeURIComponent(collection.uuid)}`}
      aria-label={tx('ui.uploads.open_kit_in_sample_tracking', { activationCode: code })}
      title={tx('ui.uploads.open_kit_in_sample_tracking', { activationCode: code })}
      onClick={(event) => event.stopPropagation()}>
      <span className="xo-upload-kit-visual" aria-hidden="true">
        {imageUrl ? <img src={imageUrl} alt="" width="28" height="28" loading="lazy" decoding="async" /> : <Package />}
      </span>
      <span className="xo-upload-kit-copy"><strong>{productName}</strong><small>{code}</small></span>
      <ChevronRight aria-hidden="true" />
    </Link>
  );
}

function between(start, end) {
  if (start === null || start === undefined || end === null || end === undefined) return '—';
  return duration(Math.max(0, Number(end) - Number(start)));
}

function relativeWhen(value, clock = Date.now()) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return '—';
  const elapsed = Math.max(0, clock - timestamp);
  if (elapsed < 60000) return tx('ui.common.just_now');
  if (elapsed < 3600000) return tx('ui.common.minutes_ago', { count: Math.floor(elapsed / 60000) });
  if (elapsed < 86400000) return tx('ui.common.hours_ago', { count: Math.floor(elapsed / 3600000) });
  return tx('ui.common.days_ago', { count: Math.floor(elapsed / 86400000) });
}

function useOperationalClock(active) {
  const [clock, setClock] = useState(Date.now());
  useEffect(() => {
    setClock(Date.now());
    if (!active) return undefined;
    const timer = window.setInterval(() => setClock(Date.now()), 5000);
    return () => window.clearInterval(timer);
  }, [active]);
  return clock;
}

function jobTiming(job, clock) {
  const state = String(job.state || 'pending');
  const runStatus = String(job.run_status || 'queued');
  const estimateMs = Math.max(0, Number(job.est_duration_minutes) || 0) * 60000;
  const startedAt = Number(job.run_started_at) || null;
  const finishedAt = Number(job.run_finished_at) || null;
  const elapsedMs = startedAt === null ? 0 : Math.max(0, (finishedAt || clock) - startedAt);

  if (state === 'complete') {
    return {
      tone: 'complete', percent: 100, label: tx("ui.uploads.results_secured_6d1ca679"),
      timing: startedAt === null
        ? tx('ui.uploads.complete') : tx('ui.uploads.completed_in', { duration: duration(elapsedMs) }),
      estimate: estimateMs > 0 ? tx('ui.uploads.estimate_duration', { duration: duration(estimateMs) }) : null,
    };
  }
  if (state === 'failed' || state === 'cancelled') {
    return {
      tone: 'failed', percent: estimateMs > 0 ? Math.min(100, (elapsedMs / estimateMs) * 100) : 100,
      label: state === 'cancelled' ? tx('ui.uploads.cancelled') : tx('ui.uploads.run_failed'),
      timing: elapsedMs > 0
        ? tx('ui.uploads.stopped_after', { duration: duration(elapsedMs) }) : tx('ui.uploads.did_not_complete'),
      estimate: null,
    };
  }
  if (state === 'offloading') {
    const offloadAt = Number(job.offload_started_at) || finishedAt || clock;
    return {
      tone: 'offloading', percent: 98, label: tx("ui.uploads.securing_results_d8bb4f06"),
      timing: tx("ui.uploads.value_offloading_9d29483b", { value: duration(Math.max(0, clock - offloadAt)) }),
      estimate: tx("ui.uploads.pipeline_complete_5af8a5b7"),
    };
  }
  if (state === 'running') {
    const ratio = estimateMs > 0 ? elapsedMs / estimateMs : 0;
    const percent = estimateMs > 0
      ? Math.min(96, Math.max(3, ratio * 92)) : 18;
    let estimate = tx('ui.uploads.no_runtime_estimate');
    if (estimateMs > 0 && elapsedMs <= estimateMs) {
      estimate = tx("ui.uploads.value_remaining_c6c8b5ef", { value: duration(estimateMs - elapsedMs) });
    } else if (estimateMs > 0) {
      estimate = tx("ui.uploads.value_past_estimate_d9503f43", { value: duration(elapsedMs - estimateMs) });
    }
    return {
      tone: 'running', percent, label: tx("ui.uploads.pipeline_running_ee5d7b5f"),
      timing: tx("ui.uploads.value_elapsed_f009e832", { value: duration(elapsedMs) }), estimate,
    };
  }

  const label = runStatus === 'downloading'
    ? tx('ui.uploads.fetching_inputs')
    : runStatus === 'assigned' ? tx('ui.uploads.worker_assigned') : tx('ui.uploads.waiting_for_worker');
  return {
    tone: 'waiting', percent: null, label,
    timing: relativeWhen(job.queued_at || job.updated_at, clock),
    estimate: estimateMs > 0 ? tx('ui.uploads.estimated_run_duration', { duration: duration(estimateMs) }) : null,
  };
}

function JobProgress({ job, clock, compact = false }) {
  const timing = jobTiming(job, clock);
  const worker = job.node_display_name || job.node_hostname || null;
  return (
    <div className={`xo-run-progress is-${timing.tone}${compact ? ' is-compact' : ''}`}>
      <div className="xo-run-progress-head">
        <span className="xo-run-icon" aria-hidden="true">
          {timing.tone === 'complete' ? <CheckCircle2 />
            : timing.tone === 'failed' ? <CircleAlert />
              : timing.tone === 'offloading' ? <CloudUpload />
                : timing.tone === 'running' ? <Loader2 className="xo-spin" /> : <Clock />}
        </span>
        <span className="xo-run-name">
          <strong>{job.display_name || job.pipeline || tx("ui.uploads.analysis_62c7fcf4")}</strong>
          <small>{job.omics || tx("ui.uploads.pipeline_32b1d5a7")} · v{job.code_version || '—'}{worker ? tx("ui.uploads.worker_42fe8c97", { worker: worker }) : ''}</small>
        </span>
        <span className="xo-run-state">{timing.label}</span>
      </div>
      <div className="xo-run-track" role="progressbar"
        aria-label={tx("ui.uploads.value_estimated_progress_fe8b38f0", { value: job.display_name || job.pipeline || tx('ui.uploads.pipeline_32b1d5a7') })}
        aria-valuemin="0" aria-valuemax="100"
        aria-valuenow={timing.percent === null ? undefined : Math.round(timing.percent)}>
        <span className={timing.percent === null ? 'is-indeterminate' : ''}
          style={timing.percent === null ? undefined : { width: `${timing.percent}%` }} />
      </div>
      <div className="xo-run-progress-foot">
        <span>{timing.timing}</span>
        {timing.estimate && <span>{timing.estimate}</span>}
        <CollectionLink collection={job.collection} className="is-progress" />
      </div>
    </div>
  );
}

function SelectMenu({ id, value, options, placeholder = tx('ui.common.select') , disabled = false, onChange }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const selected = options.find((option) => String(option.value) === String(value)) ?? null;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (root.current !== null && !root.current.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <div className={`xo-upload-select${open ? ' is-open' : ''}`} ref={root}>
      <button id={id} type="button" disabled={disabled} aria-haspopup="listbox"
        aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        <span className={selected === null ? 'is-placeholder' : ''}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div className="xo-upload-select-menu" role="listbox" aria-labelledby={id}>
          {options.map((option) => {
            const active = String(option.value) === String(value);
            return (
              <button key={String(option.value)} type="button" role="option" aria-selected={active}
                className={active ? 'is-selected' : ''}
                onClick={() => { onChange(option.value); setOpen(false); }}>
                <span><strong>{option.label}</strong>{option.detail && <small>{option.detail}</small>}</span>
                {active && <Check aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PatientPicker({ lookup, disabled }) {
  if (lookup.patient !== null) {
    return (
      <div className="xo-upload-resolved-person">
        <span aria-hidden="true"><UserRound /></span>
        <div>
          <strong>{lookup.patient.name || tx("ui.uploads.patient_id_1dbd6c4a", { id: lookup.patient.id })}</strong>
          <small>{tx("ui.uploads.patient_4814dc9a")}{lookup.patient.id} · {lookup.patient.dateOfBirth || tx("ui.uploads.no_date_of_birth_64889291")} · {lookup.patient.gender ? codeLabel('gender', lookup.patient.gender) : tx("ui.uploads.no_sex_recorded_d7442338")}</small>
        </div>
        <button type="button" disabled={disabled} onClick={lookup.reset}>{tx("ui.uploads.change_64fbd995")}</button>
      </div>
    );
  }

  return (
    <div className="xo-upload-patient-picker">
      <SearchField className="xo-upload-patient-search" value={lookup.term}
        disabled={disabled} onChange={lookup.setTerm}
        placeholder={tx("ui.uploads.search_patient_name_or_id_43dc0800")}
        label={tx("ui.uploads.search_patient_name_or_id_43dc0800")}
        autoComplete="off" spellCheck="false">
        {lookup.status === 'loading' && <Loader2 className="xo-spin" aria-hidden="true" />}
      </SearchField>
      {lookup.matches.length > 0 && lookup.status === 'choosing' && (
        <div className="xo-upload-patient-results xo-search-results" role="listbox" aria-label={tx("ui.uploads.patient_matches_35ffa732")}>
          {lookup.matches.map((match) => (
            <button key={match.id} type="button" role="option" aria-selected="false"
              onClick={() => lookup.choose(match)}>
              <span aria-hidden="true"><UserRound /></span>
              <span><strong>{match.name || tx("ui.uploads.patient_id_1dbd6c4a", { id: match.id })}</strong>
                <small>#{match.id} · {match.dateOfBirth || tx("ui.uploads.no_date_of_birth_64889291")} · {match.gender ? codeLabel('gender', match.gender) : tx("ui.uploads.no_sex_recorded_d7442338")}</small></span>
              <ChevronRight aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
      {lookup.message && lookup.status !== 'choosing' && (
        <p className={lookup.status === 'missing' ? 'is-error' : ''}>
          {lookup.message}
        </p>
      )}
    </div>
  );
}

function uploadLane(upload) {
  const jobs = Array.isArray(upload.job_summaries) ? upload.job_summaries : [];
  if (upload.status === 'failed' || upload.error_message
      || jobs.some((job) => job.state === 'failed' || job.state === 'cancelled')) return 'attention';
  if (upload.status === 'pending'
      || jobs.some((job) => ['pending', 'running', 'offloading'].includes(job.state))) return 'active';
  if (jobs.length > 0 && jobs.every((job) => job.state === 'complete')) return 'complete';
  return upload.status === 'ready' ? 'complete' : 'active';
}

function uploadLaneLabel(upload) {
  const lane = uploadLane(upload);
  const jobs = Array.isArray(upload.job_summaries) ? upload.job_summaries : [];
  if (lane === 'attention') return tx('ui.uploads.needs_attention');
  if (lane === 'complete') return jobs.length > 0
    ? tx('ui.uploads.all_analyses_complete') : tx('ui.uploads.upload_verified');
  if (upload.status === 'pending') return tx('ui.uploads.receiving_files');
  if (jobs.some((job) => job.state === 'offloading')) return tx('ui.uploads.securing_results_d8bb4f06');
  if (jobs.some((job) => job.state === 'running')) return tx('ui.uploads.analyses_running');
  return tx('ui.uploads.waiting_for_worker');
}

function UploadRailItem({ upload, clock, onOpen }) {
  const jobs = Array.isArray(upload.job_summaries) ? upload.job_summaries : [];
  const lane = uploadLane(upload);
  const activity = lane === 'complete' ? 'complete'
    : lane === 'attention' ? 'attention'
      : jobs.some((job) => job.state === 'running') ? 'running'
        : jobs.some((job) => job.state === 'offloading') ? 'offloading'
          : upload.status === 'pending' ? 'uploading' : 'waiting';
  const patient = upload.is_external
    ? tx('ui.uploads.external_analysis')
    : (upload.patient_name || (upload.patient_id
      ? tx('ui.uploads.patient_number', { id: upload.patient_id }) : tx('ui.uploads.patient_not_linked')));

  return (
    <article className={`xo-upload-rail-upload is-${lane} is-${activity}`}>
      <button type="button" onClick={onOpen}>
        <span className="xo-upload-rail-upload-icon" aria-hidden="true">
          {lane === 'complete' ? <CheckCircle2 />
            : lane === 'attention' ? <CircleAlert />
              : activity === 'running' ? <Loader2 className="xo-spin" />
                : activity === 'offloading' ? <CloudUpload /> : <TestTube2 />}
        </span>
        <span className="xo-upload-rail-upload-copy">
          <strong>{patient}</strong>
          <small>{upload.sample_name || upload.sample_code || tx("ui.uploads.sample_58fabfa7")} {tx("ui.uploads.visit_5a9174b3")} {upload.visit_number ?? '—'} · {relativeWhen(upload.created_at, clock)}</small>
        </span>
        <span className="xo-upload-rail-upload-state">{uploadLaneLabel(upload)}</span>
        <ChevronRight aria-hidden="true" />
      </button>
      <CollectionLink collection={upload.collection} className="is-rail" />
      <div className="xo-upload-rail-upload-pipelines">
        {jobs.length > 0 ? jobs.map((job) => {
          const timing = jobTiming(job, clock);
          return (
            <div key={job.uuid} className={job.state === 'failed' ? 'is-failed' : ''}>
              <p><span>{job.display_name || job.pipeline || tx("ui.uploads.analysis_62c7fcf4")}</span><small>{timing.label}</small></p>
              <div className="xo-run-track" role="progressbar"
                aria-label={tx("ui.uploads.value_estimated_progress_fe8b38f0", { value: job.display_name || job.pipeline || tx('ui.uploads.analysis_62c7fcf4') })}
                aria-valuemin="0" aria-valuemax="100"
                aria-valuenow={timing.percent === null ? undefined : Math.round(timing.percent)}>
                <span className={timing.percent === null ? 'is-indeterminate' : ''}
                  style={timing.percent === null ? undefined : { width: `${timing.percent}%` }} />
              </div>
            </div>
          );
        }) : (
          <div>
            <p><span>{tx("ui.uploads.source_files_e660d20b")}</span><small>{upload.status === 'pending' ? tx("ui.uploads.uploading_de272409") : tx("ui.uploads.verified_aed3b8c6")}</small></p>
            <div className="xo-run-track" role="progressbar" aria-label={tx("ui.uploads.upload_state_a43f4fe3")}
              aria-valuemin="0" aria-valuemax="100" aria-valuenow={upload.status === 'pending' ? undefined : 100}>
              <span className={upload.status === 'pending' ? 'is-indeterminate' : ''}
                style={upload.status === 'pending' ? undefined : { width: '100%' }} />
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function RunRailItem({ upload, job, clock, onOpen }) {
  const patient = upload.is_external
    ? tx('ui.uploads.external_analysis')
    : (upload.patient_name || (upload.patient_id
      ? tx('ui.uploads.patient_number', { id: upload.patient_id }) : tx('ui.uploads.patient_not_linked')));
  const timing = job === null ? null : jobTiming(job, clock);
  const tone = timing?.tone || 'waiting';
  return (
    <article className={`xo-upload-rail-run is-${tone}${job?.state === 'failed' ? ' is-attention' : ''}`}>
      <button type="button" onClick={onOpen}>
        <span className="xo-upload-rail-run-icon" aria-hidden="true">
          {job?.state === 'complete' ? <CheckCircle2 />
            : job?.state === 'offloading' ? <CloudUpload />
              : job?.state === 'running' ? <Loader2 className="xo-spin" />
                : job?.state === 'failed' ? <CircleAlert />
                  : job === null ? <Loader2 className="xo-spin" /> : <Clock />}
        </span>
        <span className="xo-upload-rail-run-copy">
          <strong>{job?.display_name || (upload.status === 'pending' ? tx("ui.uploads.receiving_source_files_cbb1efc7") : tx("ui.uploads.analysis_62c7fcf4"))}</strong>
          <small>{patient} · {upload.sample_name || upload.sample_code || tx("ui.uploads.sample_58fabfa7")}</small>
        </span>
        <ChevronRight aria-hidden="true" />
      </button>
      <CollectionLink collection={job?.collection ?? upload.collection} className="is-rail" />
      <div className="xo-upload-rail-progress">
        <div><span>{timing?.label || tx("ui.uploads.uploading_de272409")}</span><small>{timing?.timing || relativeWhen(upload.updated_at, clock)}</small></div>
        <div className="xo-run-track" role="progressbar" aria-label={tx("ui.uploads.estimated_progress_570fb261")}
          aria-valuemin="0" aria-valuemax="100"
          aria-valuenow={timing?.percent === null || timing === null ? undefined : Math.round(timing.percent)}>
          <span className={timing?.percent === null || timing === null ? 'is-indeterminate' : ''}
            style={timing?.percent === null || timing === null ? undefined : { width: `${timing.percent}%` }} />
        </div>
        <p>{timing?.estimate || (Number(upload.file_count) === 1
          ? tx('ui.uploads.one_source_file', { count: upload.file_count ?? 0 })
          : tx('ui.uploads.many_source_files', { count: upload.file_count ?? 0 }))}</p>
      </div>
    </article>
  );
}

function readableJson(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'string') return JSON.stringify(value, null, 2);
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function joinedJson(value) {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value !== 'string') return String(value);
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.join(', ') : String(value);
  } catch {
    return value;
  }
}

function DetailFact({ label, value, mono = false }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? 'xomics-mono xomics-wrap' : 'xomics-wrap'}>{value ?? '—'}</dd>
    </div>
  );
}

function JobDetail({ job, clock }) {
  const inputs = Array.isArray(job.inputs) ? job.inputs : [];
  const outputFiles = Array.isArray(job.output_files) ? job.output_files : [];
  const outputCount = Number(job.outputs ?? outputFiles.length) || outputFiles.length;
  const params = readableJson(job.params);
  const node = job.node_display_name || job.node || '—';
  const nodeContext = [job.node_provider, job.node_region].filter(Boolean).join(' · ');

  return (
    <article className="xo-upload-job">
      <header className="xo-upload-job-head">
        <div>
          <h4>{job.display_name || job.pipeline}{job.mode ? tx("ui.uploads.mode_d83be375", { mode: job.mode }) : ''}</h4>
          <p>
            <span className="xomics-mono">{job.uuid}</span>
            {job.code_version && tx("ui.uploads.vcode_version_1ee812dc", { code_version: job.code_version })}
            {job.release_tag && tx("ui.uploads.release_tag_5090c99f", { release_tag: job.release_tag })}
            {job.omics && tx("ui.uploads.omics_ea240661", { omics: job.omics })}
          </p>
        </div>
        <div className="xomics-chips">
          <StateChip status={job.state} />
          {job.run_status && job.run_status !== job.state && <StateChip status={job.run_status} />}
        </div>
      </header>

      <div className="xo-upload-job-progress">
        <JobProgress job={job} clock={clock} />
      </div>

      <dl className="xo-upload-facts is-job">
        <DetailFact label={tx("ui.uploads.pipeline_row_0d7d1fe8")} value={job.pipeline_id ?? '—'} />
        <DetailFact label={tx("ui.uploads.release_row_006a61e0")} value={job.release_id ?? '—'} />
        <DetailFact label={tx("ui.uploads.worker_99edd8c8")} value={nodeContext ? `${node} · ${nodeContext}` : node} />
        <DetailFact label={tx("ui.uploads.job_created_0c4c43c7")} value={when(job.created_at)} />
        <DetailFact label={tx("ui.uploads.queued_6a599877")} value={when(job.queued_at)} />
        <DetailFact label={tx("ui.uploads.assigned_e24e824b")} value={when(job.assigned_at)} />
        <DetailFact label={tx("ui.uploads.run_started_7d782583")} value={when(job.run_started_at)} />
        <DetailFact label={tx("ui.uploads.run_finished_94ae431c")} value={when(job.run_finished_at)} />
        <DetailFact label={tx("ui.uploads.queue_wait_49207c14")} value={between(job.queued_at, job.assigned_at || job.run_started_at)} />
        <DetailFact label={tx("ui.uploads.run_time_0e539012")} value={between(job.run_started_at, job.run_finished_at)} />
        <DetailFact label={tx("ui.uploads.offload_time_f5fd4788")} value={between(job.offload_started_at, job.offload_finished_at)} />
        <DetailFact label={tx("ui.uploads.offloaded_e189a518")} value={bytes(job.offload_bytes)} />
        <DetailFact label={tx("ui.uploads.exit_code_5df6a01e")} value={job.run_exit_code ?? '—'} />
        <DetailFact label={tx("ui.uploads.priority_886cbff9")} value={job.priority ?? 0} />
        <DetailFact label={tx("ui.uploads.timeout_at_14e2ce0d")} value={when(job.timeout_at)} />
        <DetailFact label={tx("ui.uploads.last_job_update_606315bd")} value={when(job.updated_at)} />
      </dl>

      {params && (
        <details className="xo-upload-technical">
          <summary>{tx("ui.uploads.parameters_used_for_this_run_770843bf")}</summary>
          <pre>{params}</pre>
        </details>
      )}

      <section className="xo-upload-job-section">
        <div className="xo-upload-section-head">
          <h5>{tx("ui.uploads.inputs_bbcf64a3")}</h5><span>{inputs.length === 1
            ? tx('ui.uploads.one_mapped_file', { count: inputs.length })
            : tx('ui.uploads.many_mapped_files', { count: inputs.length })}</span>
        </div>
        {inputs.length === 0 ? <p className="xomics-muted">{tx("ui.uploads.no_input_mapping_was_returned_52c1eee2")}</p> : (
          <div className="xomics-table-scroll"><table className="xomics-table">
            <thead><tr>
              <th>{tx("ui.uploads.slot_ece2ac10")}</th><th>{tx("ui.uploads.file_2c3cafa4")}</th><th>{tx("ui.uploads.type_3deb7456")}</th><th>{tx("ui.uploads.verified_aed3b8c6")}</th>
              <th>{tx("ui.uploads.download_a479c9c3")}</th><th>{tx("ui.uploads.bytes_8e5fdab9")}</th><th>{tx("ui.uploads.downloaded_c6197029")}</th><th>{tx("ui.uploads.error_7f2f6a15")}</th>
            </tr></thead>
            <tbody>{inputs.map((input, index) => (
              <tr key={`${input.slot_position}-${input.upload_position}-${index}`}>
                <td><strong>{input.slot_title || tx("ui.uploads.slot_slot_position_0968132e", { slot_position: input.slot_position })}</strong><small className="xo-cell-note">{tx("ui.uploads.position_slot_position_840658e1", { slot_position: input.slot_position })}</small></td>
                <td className="xomics-wrap">{input.original_name}<small className="xo-cell-note">{tx("ui.uploads.upload_file_upload_position_96d83cb5", { upload_position: input.upload_position })}</small></td>
                <td>{input.content_kind || '—'}<small className="xo-cell-note">{input.file_format || '—'}</small></td>
                <td><StateChip status={input.verify_status} /></td>
                <td><StateChip status={input.download_status} /></td>
                <td className="xomics-num">{bytes(input.bytes_downloaded || input.actual_bytes || input.declared_bytes)}</td>
                <td>{when(input.downloaded_at)}</td>
                <td className="xomics-wrap">{input.error_message || '—'}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </section>

      <section className="xo-upload-job-section">
        <div className="xo-upload-section-head">
          <h5>{tx("ui.uploads.outputs_7835db44")}</h5><span>{outputCount === 1
            ? tx('ui.uploads.one_recorded_file', { count: outputCount })
            : tx('ui.uploads.many_recorded_files', { count: outputCount })}</span>
        </div>
        {job.output_r2_key && (
          <p className="xo-upload-r2"><strong>{tx("ui.uploads.work_folder_e4452a9b")}</strong><span className="xomics-mono xomics-wrap">{job.output_r2_key}</span></p>
        )}
        {outputFiles.length === 0 ? (
          <p className="xomics-muted">{outputCount > 0
            ? outputCount === 1
              ? tx('ui.uploads.one_output_record_without_details', { count: outputCount })
              : tx('ui.uploads.many_output_records_without_details', { count: outputCount })
            : tx("ui.uploads.no_output_files_recorded_yet_523bd60e")}</p>
        ) : (
          <div className="xomics-table-scroll"><table className="xomics-table">
            <thead><tr>
              <th>#</th><th>{tx("ui.uploads.file_2c3cafa4")}</th><th>{tx("ui.uploads.declared_output_2cef12f1")}</th><th>{tx("ui.uploads.format_041a5dec")}</th>
              <th>{tx("ui.uploads.size_b7152342")}</th><th>{tx("ui.uploads.sha_256_45792d6b")}</th><th>{tx("ui.uploads.r2_object_3c22d0ee")}</th><th>{tx("ui.uploads.recorded_d5383ea7")}</th>
            </tr></thead>
            <tbody>{outputFiles.map((output) => (
              <tr key={output.id}>
                <td className="xomics-num">{output.output_position ?? '—'}</td>
                <td className="xomics-wrap">{output.filename}</td>
                <td className="xomics-wrap">{output.file_pattern || '—'}{output.output_set && <small className="xo-cell-note">{output.output_set}</small>}</td>
                <td>{output.file_format || '—'}</td>
                <td className="xomics-num">{bytes(output.bytes)}</td>
                <td className="xomics-mono xomics-wrap">{output.sha256 || '—'}</td>
                <td className="xomics-mono xomics-wrap">{output.r2_key}</td>
                <td>{when(output.created_at)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </section>

      {(job.failed_step || job.error_message) && (
        <div className="xomics-auth-error xo-upload-job-error">
          <strong>{job.failed_step ? tx("ui.uploads.failed_during_failed_step_670d29b3", { failed_step: job.failed_step }) : tx("ui.uploads.job_error_31d46dc9")}</strong>
          {job.error_message && <pre>{job.error_message}</pre>}
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------- new upload

function NewUpload({
  catalogue, catalogueReady, linkedUpload, onClearLinkedUpload, onDone, requestedCode = null, onRequestedCodeUsed = () => {},
}) {
  const [sourceMode, setSourceMode] = useState('collection');
  const [journeyStep, setJourneyStep] = useState(linkedUpload === null ? 'source' : 'identity');
  const [collectionCode, setCollectionCode] = useState('');
  const [collectionSample, setCollectionSample] = useState(null);
  // The tube whose code was entered, when it was a tube's, and the lab work of the collection.
  const [collectionTube, setCollectionTube] = useState(null);
  const [collectionWork, setCollectionWork] = useState([]);
  const [collectionLoading, setCollectionLoading] = useState(false);
  const [collectionError, setCollectionError] = useState('');
  const [sampleCode, setSampleCode] = useState('');
  const [selected, setSelected] = useState([]);
  // A patient is chosen, never typed: the record resolves to patient_metadata.id,
  // which is what the upload's foreign key stores. Same lookup the reports screen
  // uses -- one endpoint, one set of rules.
  const patient = usePatientLookup();
  const [chosen, setChosen] = useState({});
  const [history, setHistory] = useState(null);
  // What the operator picked, seeded from the server's suggestion.
  const [visit, setVisit] = useState(1);
  // A clinic visit barcode carries its visit, and its upload is that visit: the
  // number is shown, not chosen (the server keeps it whatever is sent).
  const [barcodeVisit, setBarcodeVisit] = useState(null);
  const [step, setStep] = useState(STEPS.idle);
  const [progress, setProgress] = useState({});
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const appliedLink = useRef('');
  const collectionLookupTimer = useRef(null);
  const collectionLookupSequence = useRef(0);
  // The catalogue as it stands when a collection is resolved; a later refresh must not reset what was chosen.
  const catalogueNow = useRef(catalogue);
  catalogueNow.current = catalogue;
  const [linkedSample, setLinkedSample] = useState(null);
  const [linkedWork, setLinkedWork] = useState([]);
  const [linkedSampleError, setLinkedSampleError] = useState('');
  const [linkedPatientError, setLinkedPatientError] = useState('');
  // A customer's sample ({ reportsAsked }, from GET /Samples/:uuid), or null.
  const [collectionCustomer, setCollectionCustomer] = useState(null);
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const resolvedCollection = linkedSample ?? collectionSample;
  const resolvedCustomer = linkedSample === null ? collectionCustomer : linkedCustomer;
  const resolvedTube = linkedSample === null ? collectionTube : null;
  const uploadPipelines = useMemo(() => resolvedCollection === null ? { offered: [], own: [], workflows: [] }
    : collectionUploadPipelines(resolvedCollection, linkedSample === null ? collectionWork : linkedWork, resolvedTube),
  [resolvedCollection, linkedSample, collectionWork, linkedWork, resolvedTube]);
  const collectionOffered = useMemo(() => resolvedCollection === null ? [] : offeredPipelines(catalogue, uploadPipelines, resolvedCollection),
    [catalogue, uploadPipelines, resolvedCollection]);
  // A kit runs the pipelines of its product, a customer's sample every pipeline of
  // the workflow they ordered: both come ticked and cannot be changed (Emre,
  // 2026-11-03). Any other collection's are the operator's choice.
  const pipelinesFixed = sourceMode === 'collection' && resolvedCollection !== null
    && (resolvedCollection.product !== null || resolvedCustomer !== null);
  const linkedPipelineUnavailable = sourceMode === 'collection' && resolvedCollection !== null
    && catalogueReady && collectionOffered.length === 0;

  useEffect(() => () => {
    if (collectionLookupTimer.current !== null) window.clearTimeout(collectionLookupTimer.current);
  }, []);

  useEffect(() => {
    if (linkedUpload?.sampleUuid === undefined) {
      setLinkedSample(null);
      setLinkedSampleError('');
      return undefined;
    }
    let active = true;
    setLinkedSample(null);
    setLinkedSampleError('');
    loadSample(linkedUpload.sampleUuid)
      .then(({ sample, labWorkflows, customerSample }) => {
        if (!active) return;
        if (Number(sample.collectionId) !== linkedUpload.collectionId
            || Number(sample.patientId) !== linkedUpload.patientId) {
          setLinkedSampleError(tx("ui.uploads.this_sample_changed_after_the_shortcut_was_opened_retu_38647145"));
          return;
        }
        if (customerSample !== null && !customerSample.reportsAsked) {
          setLinkedSampleError(tx('ui.uploads.customer_report_not_asked'));
          return;
        }
        setLinkedWork(labWorkflows);
        setLinkedCustomer(customerSample);
        setLinkedSample(sample);
      })
      .catch((requestError) => {
        if (active) setLinkedSampleError(errorMessage(requestError));
      });
    return () => { active = false; };
  }, [linkedUpload]);

  // The URL is only a convenience. Seed the pipeline immediately, then resolve
  // the patient through the exact-id endpoint. Passing a one-digit database id
  // through free-text search fails its minimum-length contract and is not an
  // authoritative way to select a foreign-key row in any case.
  useEffect(() => {
    if (linkedUpload === null || appliedLink.current === linkedUpload.key) return;
    appliedLink.current = linkedUpload.key;
    setSourceMode('collection');
    setJourneyStep('identity');
    setSelected([]);
    setSampleCode('');
    setChosen({});
    setResult(null);
    setError('');
  }, [linkedUpload]);

  useEffect(() => {
    if (linkedSample !== null) {
      const start = collectionUploadStart(linkedSample, collectionUploadPipelines(linkedSample, linkedWork, null), catalogueNow.current,
        { all: linkedCustomer !== null });
      setCollectionCode(linkedSample.code);
      setSampleCode(start.sampleCode);
      setSelected(start.selected);
    }
  }, [linkedSample, linkedWork, linkedCustomer]);

  useEffect(() => {
    if (linkedUpload === null) {
      setLinkedPatientError('');
      return undefined;
    }
    let active = true;
    setLinkedPatientError('');
    lookupReportPatient(linkedUpload.patientId)
      .then((resolved) => {
        if (!active) return;
        if (Number(resolved.id) !== linkedUpload.patientId) {
          setLinkedPatientError(tx("ui.uploads.the_linked_patient_did_not_match_this_sample_return_to_580edd27"));
          return;
        }
        patient.choose(resolved);
      })
      .catch((requestError) => {
        if (active) setLinkedPatientError(errorMessage(requestError));
      });
    return () => { active = false; };
  }, [linkedUpload]);

  // A code handed over by a link (a requested upload, or the notification of
  // one) is looked up as if it had been typed and Enter pressed; the matching
  // does the rest. An upload already on its way is never interrupted by one.
  useEffect(() => {
    if (requestedCode === null) return;
    onRequestedCodeUsed();
    if (linkedUpload !== null || step !== STEPS.idle) return;
    if (collectionLookupTimer.current !== null) window.clearTimeout(collectionLookupTimer.current);
    collectionLookupTimer.current = null;
    setSourceMode('collection');
    setJourneyStep('identity');
    setHistory(null);
    setResult(null);
    setError('');
    setCollectionCode(requestedCode);
    resolveCollection(requestedCode);
    // Only a newly handed-over code starts this; the form's own state must not start it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedCode]);

  function changeSourceMode(nextMode) {
    if (linkedUpload !== null) return;
    collectionLookupSequence.current += 1;
    if (collectionLookupTimer.current !== null) window.clearTimeout(collectionLookupTimer.current);
    collectionLookupTimer.current = null;
    setCollectionLoading(false);
    if (nextMode === sourceMode) {
      setJourneyStep('identity');
      return;
    }
    setSourceMode(nextMode);
    setCollectionSample(null);
    setCollectionCustomer(null);
    setCollectionTube(null);
    setCollectionWork([]);
    setCollectionCode('');
    setCollectionError('');
    setBarcodeVisit(null);
    patient.reset();
    setSampleCode('');
    setSelected([]);
    setChosen({});
    setHistory(null);
    setResult(null);
    setError('');
    setJourneyStep('identity');
  }

  function clearCollection() {
    if (linkedUpload !== null) return;
    collectionLookupSequence.current += 1;
    if (collectionLookupTimer.current !== null) window.clearTimeout(collectionLookupTimer.current);
    collectionLookupTimer.current = null;
    setCollectionLoading(false);
    setCollectionSample(null);
    setCollectionCustomer(null);
    setCollectionTube(null);
    setCollectionWork([]);
    setCollectionCode('');
    setCollectionError('');
    setBarcodeVisit(null);
    patient.reset();
    setSampleCode('');
    setSelected([]);
    setChosen({});
    setHistory(null);
    setResult(null);
  }

  // `typing` is a look-up made in a pause while the code is being typed: a code
  // that names nothing yet is not an error until Enter asks for it.
  async function resolveCollection(code, { typing = false } = {}) {
    if (code === null) return;
    const sequence = ++collectionLookupSequence.current;
    setCollectionLoading(true);
    setCollectionError('');
    setCollectionSample(null);
    setCollectionCustomer(null);
    setCollectionTube(null);
    setCollectionWork([]);
    setBarcodeVisit(null);
    patient.reset();
    setSampleCode('');
    setSelected([]);
    setChosen({});
    try {
      const collection = await findCollectionByCode(code);
      if (sequence !== collectionLookupSequence.current) return;
      setBarcodeVisit(Number.isSafeInteger(collection.clinicVisitNumber) ? collection.clinicVisitNumber : null);
      if (collection.activatedAt === null || collection.patientId === null) {
        setCollectionError(tx("ui.uploads.this_kit_has_not_been_activated_for_a_patient_yet_262cfe6a"));
        return;
      }
      const [{ sample, labWorkflows, customerSample }, resolvedPatient] = await Promise.all([
        loadSample(collection.uuid),
        lookupReportPatient(collection.patientId),
      ]);
      if (sequence !== collectionLookupSequence.current) return;
      // A customer's sample is uploaded once its customer has said yes to a report for it (Uploads.js refuses it before).
      if (customerSample !== null && !customerSample.reportsAsked) {
        setCollectionError(tx('ui.uploads.customer_report_not_asked'));
        return;
      }
      const start = collectionUploadStart(sample, collectionUploadPipelines(sample, labWorkflows, collection.tubeCode), catalogueNow.current,
        { all: customerSample !== null });
      setCollectionWork(labWorkflows);
      setCollectionTube(collection.tubeCode);
      setCollectionCustomer(customerSample);
      setCollectionSample(sample);
      setCollectionCode(collection.printedTubeCode ?? collection.tubeCode ?? sample.printedCode ?? sample.code);
      patient.choose(resolvedPatient);
      setSampleCode(start.sampleCode);
      setSelected(start.selected);
    } catch (requestError) {
      const unfinished = typing && requestError instanceof XomicsApiError && requestError.status === 404;
      if (sequence === collectionLookupSequence.current && !unfinished) setCollectionError(errorMessage(requestError));
    } finally {
      if (sequence === collectionLookupSequence.current) setCollectionLoading(false);
    }
  }

  function changeCollectionCode(value) {
    const code = value.toUpperCase().replace(/\s+/g, '');
    collectionLookupSequence.current += 1;
    if (collectionLookupTimer.current !== null) window.clearTimeout(collectionLookupTimer.current);
    collectionLookupTimer.current = null;
    setCollectionLoading(false);
    setCollectionCode(code);
    setCollectionError('');
    const lookup = collectionLookupCode(code);
    if (lookup !== null) {
      collectionLookupTimer.current = window.setTimeout(() => {
        collectionLookupTimer.current = null;
        resolveCollection(lookup, { typing: true });
      }, 700);
    }
  }

  function resolveCollectionOnEnter(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (collectionLookupTimer.current !== null) window.clearTimeout(collectionLookupTimer.current);
    collectionLookupTimer.current = null;
    resolveCollection(collectionLookupCode(collectionCode));
  }

  // Only pipelines this sample type permits, so an impossible combination cannot
  // be chosen rather than being rejected after the fact.
  const available = useMemo(
    () => (sourceMode === 'patient' ? catalogue.pipelines : collectionOffered).filter(p => p.sample_codes.includes(sampleCode)),
    [catalogue.pipelines, collectionOffered, sampleCode, sourceMode],
  );

  // What the chosen analyses need to know about the patient, from each pipeline's
  // own requires_patient_fields. Only a pipeline needing a fact the record lacks
  // holds the upload back, and the message names it.
  const factNeeds = available
    .filter((pipeline) => selected.includes(pipeline.id))
    .map((pipeline) => ({ name: pipeline.display_name, facts: requiredPatientFacts(pipeline.requires_patient_fields) }))
    .filter((need) => need.facts.length > 0);
  const missingFacts = patient.patient === null ? []
    : missingPatientFacts(patient.patient, [...new Set(factNeeds.flatMap((need) => need.facts))]);
  const missingFactsNotice = missingFacts.length === 0 ? ''
    : `${factNeeds.filter((need) => missingPatientFacts(patient.patient, need.facts).length > 0)
      .map((need) => need.name).join(', ')} · ${missingFactsMessage(patient.patient, missingFacts)}`;

  // A collection uploads as its own sample type. Only where no offered pipeline
  // takes that type (a tube whose workflow feeds a pipeline of another type) are
  // the types of the offered pipelines the choice.
  const sampleTypes = useMemo(() => {
    if (sourceMode === 'patient') return catalogue.sampleTypes;
    const own = resolvedCollection?.sampleType.code;
    return collectionOffered.length === 0 || collectionOffered.some(p => p.sample_codes.includes(own))
      ? catalogue.sampleTypes.filter(type => type.code === own)
      : catalogue.sampleTypes.filter(type => collectionOffered.some(p => p.sample_codes.includes(type.code)));
  }, [catalogue.sampleTypes, collectionOffered, resolvedCollection, sourceMode]);

  // One picker per distinct input across every chosen analysis, merged on what the
  // file means rather than what format it is. Three analyses wanting the same
  // called VCF share one picker; a transcriptome's RNA reads get their own even
  // though they are also fastq, because handing RNA to a variant caller produces a
  // result rather than an error.
  //
  // Each picker remembers which pipeline slots it satisfies, and that list is what
  // gets sent as `fills` -- the form knows the mapping because it built it, so the
  // server never has to guess.
  const slots = useMemo(() => {
    const picked = available.filter((p) => selected.includes(p.id));
    const merged = new Map();
    for (const pipeline of picked) {
      for (const input of pipeline.inputs) {
        const key = `${input.content_kind}#${input.position}`;
        const ref = `${pipeline.key}:${input.position}`;
        const acceptsMultiple = Boolean(input.accepts_multiple);
        const existing = merged.get(key);
        if (existing) {
          existing.fills.push(ref);
          if (acceptsMultiple) existing.multipleFills.push(ref);
          existing.accepts_multiple = existing.accepts_multiple || acceptsMultiple;
          existing.neededBy.push(pipeline.display_name);
          // Required by any pipeline means required: the one that needs it cannot
          // run without it, whatever the others say.
          existing.is_required = existing.is_required || Boolean(input.is_required);
          continue;
        }
        merged.set(key, {
          key,
          title: input.title,
          file_format: input.file_format,
          content_kind: input.content_kind,
          content_kind_label: input.content_kind_label,
          is_required: Boolean(input.is_required),
          accepts_multiple: acceptsMultiple,
          fills: [ref],
          multipleFills: acceptsMultiple ? [ref] : [],
          neededBy: [pipeline.display_name],
        });
      }
    }
    // Stable order so the pickers do not jump around as analyses are ticked.
    return [...merged.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [available, selected]);

  // The visit number is a default, not a computed value: one saliva swab
  // sequenced to FASTQ and variant-called to VCF is two uploads but one
  // collection, and only the operator knows that.
  useEffect(() => {
    if (!patient.rowId || !sampleCode) {
      setHistory(null);
      return undefined;
    }
    let cancelled = false;
    loadNextVisit(patient.rowId, sampleCode)
      .then((body) => {
        if (cancelled) return;
        setHistory(body);
        // Reset to the suggestion each time the patient or sample type
        // changes, so a number chosen for a different patient cannot linger.
        // A clinic visit barcode's own visit wins.
        setVisit(barcodeVisit ?? (Number(body.next_visit) || 1));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [patient.rowId, sampleCode, barcodeVisit]);

  // Germline reads do not change, so a second result supersedes the first rather
  // than extending it. Worth saying out loud; never worth refusing.
  const repeated = useMemo(() => {
    if (!history) return [];
    return history.completed.filter(
      (done) => !done.is_repeatable && selected.includes(done.id),
    );
  }, [history, selected]);

  // Selection carries the pipeline id -- the row the catalogue offered for the
  // active release. name:mode names a family, not a version.
  function toggle(id) {
    setSelected((current) => (current.includes(id)
      ? current.filter((k) => k !== id)
      : [...current, id]));
    setChosen({});
    setResult(null);
  }

  const ready = !linkedPipelineUnavailable
    && (sourceMode === 'patient' || resolvedCollection !== null)
    && linkedSampleError === ''
    && linkedPatientError === ''
    && sampleCode && selected.length > 0
    && slots.filter((s) => s.is_required).every((s) => hasFiles(chosen[s.key]))
    && patient.rowId !== null
    && missingFacts.length === 0;

  async function submit(event) {
    event.preventDefault();
    setError('');
    setResult(null);

    // position is now only this file's index in the upload; which pipeline slots it
    // satisfies is carried by `fills`.
    const picked = slots.filter((slot) => hasFiles(chosen[slot.key]));
    const files = [];
    // The server answers by position, but progress is displayed against the
    // picker, so the two have to be tied together here where both are known.
    const byPosition = new Map();
    for (const slot of picked) {
      const selectedFiles = Array.isArray(chosen[slot.key])
        ? chosen[slot.key] : [chosen[slot.key]];
      selectedFiles.forEach((file, index) => {
        // The first file can satisfy every pipeline sharing this semantic picker.
        // Further files only satisfy pipelines whose exact slot opts into
        // accepts_multiple, so an R1/R2 or other single-file input stays singular.
        const fills = index === 0 ? slot.fills : slot.multipleFills;
        if (fills.length === 0) return;
        const position = files.length + 1;
        const progressKey = `${slot.key}#${index + 1}`;
        files.push({
          position,
          fills,
          original_name: file.name,
          declared_bytes: file.size,
        });
        byPosition.set(position, { file, key: progressKey });
      });
    }

    try {
      setStep(STEPS.creating);
      const created = await createXomicsUpload({
        sample_code: sampleCode,
        pipelines: selected,
        patient_id: patient.rowId,
        collection_id: resolvedCollection?.collectionId ?? null,
        visit_number: visit,
        files,
      });

      // Straight to object storage. A 20 GB FASTQ never passes through the API.
      // Every file goes up in parts, because R2 refuses a single PUT over 5 GiB and
      // a FASTQ crosses that routinely. The parts only become an object once the
      // API assembles them, so that call is part of uploading the file, not a
      // separate step the user could be left half-way through.
      setStep(STEPS.uploading);
      for (const signed of created.files) {
        const local = byPosition.get(signed.position);
        const parts = await putSignedFile(signed, local.file, (fraction) => {
          setProgress((current) => ({ ...current, [local.key]: fraction }));
        });
        await assembleXomicsFile(created.uuid, signed.position, parts);
      }

      // Only now does anything become a job -- and only if every object is
      // present and the size it was declared to be.
      setStep(STEPS.verifying);
      const completed = await completeXomicsUpload(created.uuid);
      patient.reset();
      // Echoed back from the row rather than from what was sent, so what is shown
      // is what was stored.
      setResult({
        uuid: created.uuid,
        visit: completed.visit_number,
        jobs: completed.jobs || [],
      });
      setStep(STEPS.idle);
      setChosen({});
      setProgress({});
      onDone();
    } catch (requestError) {
      setError(errorMessage(requestError));
      setStep(STEPS.idle);
    }
  }

  const busy = step !== STEPS.idle;
  const sourceReady = patient.rowId !== null
    && (sourceMode === 'patient' || resolvedCollection !== null);
  const specimenReady = sourceReady && sampleCode !== '';
  const analysesReady = selected.length > 0;
  const requiredSlots = slots.filter((slot) => slot.is_required);
  const filesReady = requiredSlots.length > 0
    && requiredSlots.every((slot) => hasFiles(chosen[slot.key]));
  const pickedFiles = slots.flatMap((slot) => {
    const files = hasFiles(chosen[slot.key])
      ? (Array.isArray(chosen[slot.key]) ? chosen[slot.key] : [chosen[slot.key]]) : [];
    return files.map((file, index) => ({
      file,
      progress: Number(progress[`${slot.key}#${index + 1}`]) || 0,
    }));
  });
  const pickedBytes = pickedFiles.reduce((sum, entry) => sum + entry.file.size, 0);
  const transferredBytes = pickedFiles.reduce(
    (sum, entry) => sum + (entry.file.size * entry.progress), 0,
  );
  const transferPercent = step === STEPS.creating ? 3
    : step === STEPS.verifying ? 98
      : pickedBytes > 0 ? Math.max(4, (transferredBytes / pickedBytes) * 94) : 4;
  const submitLabel = step === STEPS.creating ? tx('ui.uploads.preparing_secure_upload')
    : step === STEPS.uploading ? tx('ui.uploads.uploading_percent', { percent: Math.round(transferPercent) })
      : step === STEPS.verifying ? tx('ui.uploads.verifying_and_queueing') : tx('ui.uploads.upload');
  const configurationReady = specimenReady && analysesReady
    && !linkedPipelineUnavailable && linkedSampleError === '' && linkedPatientError === ''
    && missingFacts.length === 0;
  const journeySteps = sourceMode === 'collection'
    ? [{ key: 'source', label: tx("ui.uploads.start_952f3754") }, { key: 'identity', label: tx("ui.uploads.kit_91bf6f55") }, { key: 'files', label: tx("ui.uploads.files_6ce6c512") }]
    : [{ key: 'source', label: tx("ui.uploads.start_952f3754") }, { key: 'identity', label: tx("ui.uploads.patient_de5c57ff") }, { key: 'configure', label: tx("ui.uploads.analysis_62c7fcf4") }, { key: 'files', label: tx("ui.uploads.files_6ce6c512") }];
  const currentJourneyIndex = Math.max(0, journeySteps.findIndex(({ key }) => key === journeyStep));
  const selectedPipelines = available.filter((pipeline) => selected.includes(pipeline.id));
  const visitOptions = history === null ? []
    : barcodeVisit !== null ? [{ value: barcodeVisit, label: tx("ui.uploads.visit_number_debf9d43", { number: barcodeVisit }), detail: collectionCode }]
      : Array.from({ length: Math.max(history.next_visit, 1) }, (_unused, index) => index + 1)
        .map((number) => ({
          value: number,
          label: tx("ui.uploads.visit_number_debf9d43", { number: number }),
          detail: number === history.next_visit ? tx('ui.uploads.suggested') : tx('ui.uploads.existing'),
        }));
  const visitPlaceholder = sampleCode === ''
    ? tx('ui.uploads.choose_sample_type_first') : tx('ui.uploads.preparing_visit');

  function chooseSampleType(nextCode) {
    setSampleCode(nextCode);
    // Fixed pipelines (a kit's, a customer's) follow the sample type; others start empty.
    setSelected(pipelinesFixed
      ? collectionUploadSelection(uploadPipelines, collectionOffered, nextCode, { all: resolvedCustomer !== null })
      : []);
    setChosen({});
    setResult(null);
  }

  function startAnotherUpload() {
    appliedLink.current = '';
    onClearLinkedUpload();
    patient.reset();
    setSourceMode('collection');
    setJourneyStep('source');
    setCollectionCode('');
    setCollectionSample(null);
    setCollectionTube(null);
    setCollectionWork([]);
    setCollectionError('');
    setSampleCode('');
    setSelected([]);
    setChosen({});
    setHistory(null);
    setVisit(1);
    setProgress({});
    setError('');
    setResult(null);
  }

  return (
    <section className={`xo-upload-journey${journeyStep === 'source' && result === null ? ' is-entry' : ''}`}>
      {journeyStep !== 'source' && result === null && (
        <nav className="xo-upload-journey-progress" aria-label={tx("ui.uploads.upload_progress_927d75fe")}>
          {journeySteps.map(({ key, label }, index) => (
            <React.Fragment key={key}>
              {index > 0 && <span className={index <= currentJourneyIndex ? 'is-complete' : ''} />}
              <div className={index === currentJourneyIndex ? 'is-current' : index < currentJourneyIndex ? 'is-complete' : ''}>
                <i>{index < currentJourneyIndex ? <Check aria-hidden="true" /> : index + 1}</i>
              <small>{label}</small>
            </div>
          </React.Fragment>
          ))}
        </nav>
      )}

      <form onSubmit={submit} className="xo-upload-journey-form">
        {result ? (
          <section className="xo-upload-journey-stage is-success">
            <span className="xo-upload-journey-success-icon"><CheckCircle2 aria-hidden="true" /></span>
            <h2>{tx("ui.uploads.upload_queued_f2bb73eb")}</h2>
            <p>{result.jobs.length === 1
              ? tx('ui.uploads.one_job_created_for_upload', { count: result.jobs.length, upload: result.uuid.slice(0, 8) })
              : tx('ui.uploads.many_jobs_created_for_upload', { count: result.jobs.length, upload: result.uuid.slice(0, 8) })}</p>
            <button type="button" className="xo-btn" onClick={startAnotherUpload}>{tx("ui.uploads.start_another_upload_078815e8")}</button>
          </section>
        ) : (
          <section className={`xo-upload-journey-stage is-${journeyStep}`}>
            {journeyStep === 'source' && (
              <>
                <header className="xo-upload-entry-mark">
                  <span aria-hidden="true"><CloudUpload /></span>
                  <h2>{tx('ui.uploads.select_upload_type')}</h2>
                </header>
                <div className="xo-upload-route-options">
                  <button type="button" disabled={busy} onClick={() => changeSourceMode('collection')}>
                    <span className="xo-upload-route-icon"><Package aria-hidden="true" /></span>
                    <span className="xo-upload-route-copy"><strong>{tx("ui.uploads.kit_id_0fe27d05")}</strong><small>{tx("ui.uploads.find_an_activated_kit_with_its_assigned_pipeline_35364266")}</small></span>
                    <ChevronRight aria-hidden="true" />
                  </button>
                  <button type="button" disabled={busy} onClick={() => changeSourceMode('patient')}>
                    <span className="xo-upload-route-icon"><UserRound aria-hidden="true" /></span>
                    <span className="xo-upload-route-copy"><strong>{tx("ui.uploads.patient_de5c57ff")}</strong><small>{tx("ui.uploads.choose_a_patient_sample_type_and_pipelines_b60c6d99")}</small></span>
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </>
            )}

            {journeyStep === 'identity' && sourceMode === 'collection' && (
              <>
                <header className="xo-upload-journey-stage-head"><h2>{tx(resolvedCollection === null ? "ui.uploads.enter_kit_id_ccf29581" : "ui.uploads.confirm_the_kit_5677723d")}</h2></header>
                {resolvedCollection === null ? (
                  <div className="xo-upload-kit-lookup">
                    <div><input value={collectionCode} disabled={busy}
                      placeholder={tx("ui.uploads.gh446301_ac566e85")} aria-label={tx("ui.uploads.kit_id_0fe27d05")} autoFocus autoComplete="off"
                      onChange={(event) => changeCollectionCode(event.target.value)}
                      onKeyDown={resolveCollectionOnEnter} />
                      {collectionLoading && <Loader2 className="xo-upload-kit-inline-loader xo-spin" aria-hidden="true" />}
                    </div>
                  </div>
                ) : (
                  <div className="xo-upload-kit-stage">
                    <div className="xo-upload-resolved-kit">
                      <span className="xo-upload-kit-image">
                        {resolvedCollection.product?.mainImageThumbnailUrl
                          ? <img src={resolvedCollection.product?.mainImageThumbnailUrl} alt="" width="52" height="52" />
                          : <Package aria-hidden="true" />}
                      </span>
                      <div>
                        <strong>{resolvedCollection.code}</strong>
                        <span>{resolvedCollection.product === null ? resolvedCollection.sampleType.name : resolvedCollection.product.name}</span>
                        <small>{patient.patient?.name || tx("ui.uploads.patient_patientid_044a44d7", { patientId: resolvedCollection.patientId })} · {resolvedCollection.currentStage?.label ?? tx("ui.uploads.activated_a0b861f6")}</small>
                        {uploadPipelines.workflows.length > 0 && <small>{tx('ui.uploads.tube.workflows', { workflows: uploadPipelines.workflows.join(', ') })}</small>}
                      </div>
                      {linkedUpload === null && <button type="button" disabled={busy} onClick={clearCollection}>{tx("ui.uploads.change_64fbd995")}</button>}
                    </div>
                    <div className="xo-upload-journey-fields">
                      <Field id="kit-sample-code" label={tx("ui.uploads.sample_type_6676959c")}>
                        <SelectMenu id="kit-sample-code" value={sampleCode} disabled={busy || sampleTypes.length < 2}
                          placeholder={tx("ui.uploads.choose_sample_type_2f38f8ef")}
                          options={sampleTypes.map((type) => ({ value: type.code, label: type.name, detail: type.code }))}
                          onChange={chooseSampleType} />
                      </Field>
                      <Field id="kit-visit-number" label={tx("ui.uploads.collection_visit_45620a70")}>
                        <SelectMenu id="kit-visit-number" value={history === null ? '' : visit}
                          disabled={busy || history === null || barcodeVisit !== null} placeholder={visitPlaceholder}
                          options={visitOptions} onChange={(nextVisit) => setVisit(Number(nextVisit))} />
                      </Field>
                    </div>
                    {sampleCode && (
                  <div className="xomics-analyses xo-upload-analysis-options">
                    {pipelinesFixed && <p className="xo-upload-inline-status">{tx(resolvedCustomer !== null
                      ? 'ui.uploads.pipelines_fixed_customer' : 'ui.uploads.pipelines_fixed_product')}</p>}
                    {available.map((pipeline) => (
                      <label key={pipeline.key} className={`xomics-analysis${selected.includes(pipeline.id) ? ' is-selected' : ''}`}>
                        <input type="checkbox" checked={selected.includes(pipeline.id)} disabled={busy || pipelinesFixed} onChange={() => toggle(pipeline.id)} />
                        <span className="xomics-analysis-body">
                          <span className="xomics-analysis-head">
                            <span className="xo-upload-analysis-title">{pipeline.display_name}<span className="xomics-analysis-version">v{pipeline.code_version}</span></span>
                            <span className="xo-upload-analysis-runtime"><Timer aria-hidden="true" />~{duration(Number(pipeline.est_duration_minutes) * 60000)}</span>
                          </span>
                          {pipeline.description && <span className="xomics-analysis-desc">{pipeline.description}</span>}
                          <span className="xomics-analysis-meta"><span>{pipeline.omics}</span></span>
                        </span>
                      </label>
                    ))}
                  </div>
                    )}
                  </div>
                )}
                {linkedUpload !== null && linkedSample === null && linkedSampleError === '' && (
                  <p className="xo-upload-inline-status" role="status"><Loader2 className="xo-spin" aria-hidden="true" />{tx("ui.uploads.loading_the_linked_kit_851287a8")}</p>
                )}
                {collectionError && <p className="xo-upload-inline-error" role="alert">{collectionError}</p>}
                {linkedSampleError && <p className="xo-upload-inline-error" role="alert">{linkedSampleError}</p>}
                {linkedPatientError && <p className="xo-upload-inline-error" role="alert">{linkedPatientError}</p>}
                {missingFactsNotice && <p className="xo-upload-inline-error" role="alert">{missingFactsNotice}</p>}
                {linkedPipelineUnavailable && (
                  <p className="xo-upload-inline-error" role="alert">{catalogue.release === null
                    ? tx("ui.uploads.no_pipeline_release_is_active_for_this_kit_015176a5")
                    : uploadPipelines.own.length === 0 ? tx('ui.uploads.tube.no_pipeline')
                      : tx("ui.uploads.this_kit_s_pipeline_is_not_available_in_the_active_rel_75709394")}</p>
                )}
                <footer className="xo-upload-journey-actions">
                  {linkedUpload === null && <button type="button" className="is-secondary" disabled={busy} onClick={() => setJourneyStep('source')}>{tx("ui.uploads.back_b52b36b7")}</button>}
                  <button type="button" className="xo-btn" disabled={!configurationReady || busy} onClick={() => setJourneyStep('files')}>{tx("ui.uploads.continue_2e026239")}<ChevronRight aria-hidden="true" /></button>
                </footer>
              </>
            )}

            {journeyStep === 'identity' && sourceMode === 'patient' && (
              <>
                <header className="xo-upload-journey-stage-head"><h2>{tx("ui.uploads.choose_the_patient_8bd19775")}</h2></header>
                <PatientPicker lookup={patient} disabled={busy} />
                <footer className="xo-upload-journey-actions">
                  <button type="button" className="is-secondary" disabled={busy} onClick={() => setJourneyStep('source')}>{tx("ui.uploads.back_b52b36b7")}</button>
                  <button type="button" className="xo-btn" disabled={!sourceReady || busy} onClick={() => setJourneyStep('configure')}>{tx("ui.uploads.continue_2e026239")}<ChevronRight aria-hidden="true" /></button>
                </footer>
              </>
            )}

            {journeyStep === 'configure' && sourceMode === 'patient' && (
              <>
                <header className="xo-upload-journey-stage-head"><h2>{tx("ui.uploads.choose_sample_and_pipelines_161349d9")}</h2></header>
                <div className="xo-upload-journey-fields">
                  <Field id="patient-sample-code" label={tx("ui.uploads.sample_type_6676959c")}>
                    <SelectMenu id="patient-sample-code" value={sampleCode} disabled={busy}
                      placeholder={tx("ui.uploads.choose_sample_type_2f38f8ef")}
                      options={sampleTypes.map((type) => ({ value: type.code, label: type.name, detail: type.code }))}
                      onChange={chooseSampleType} />
                  </Field>
                  <Field id="patient-visit-number" label={tx("ui.uploads.collection_visit_45620a70")}>
                    <SelectMenu id="patient-visit-number" value={history === null ? '' : visit}
                      disabled={busy || history === null} placeholder={visitPlaceholder}
                      options={visitOptions} onChange={(nextVisit) => setVisit(Number(nextVisit))} />
                  </Field>
                </div>
                {catalogue.release === null ? (
                  <p className="xo-upload-inline-error">{tx("ui.uploads.no_pipeline_release_is_active_7917f1fc")}</p>
                ) : sampleCode && available.length === 0 ? (
                  <p className="xomics-muted">{tx("ui.uploads.no_active_pipeline_accepts_this_sample_type_652b13f0")}</p>
                ) : sampleCode && (
                  <div className="xomics-analyses xo-upload-analysis-options">
                    {pipelinesFixed && <p className="xo-upload-inline-status">{tx(resolvedCustomer !== null
                      ? 'ui.uploads.pipelines_fixed_customer' : 'ui.uploads.pipelines_fixed_product')}</p>}
                    {available.map((pipeline) => (
                      <label key={pipeline.key} className={`xomics-analysis${selected.includes(pipeline.id) ? ' is-selected' : ''}`}>
                        <input type="checkbox" checked={selected.includes(pipeline.id)} disabled={busy || pipelinesFixed} onChange={() => toggle(pipeline.id)} />
                        <span className="xomics-analysis-body">
                          <span className="xomics-analysis-head">
                            <span className="xo-upload-analysis-title">{pipeline.display_name}<span className="xomics-analysis-version">v{pipeline.code_version}</span></span>
                            <span className="xo-upload-analysis-runtime"><Timer aria-hidden="true" />~{duration(Number(pipeline.est_duration_minutes) * 60000)}</span>
                          </span>
                          {pipeline.description && <span className="xomics-analysis-desc">{pipeline.description}</span>}
                          <span className="xomics-analysis-meta"><span>{pipeline.omics}</span></span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                {repeated.length > 0 && (
                  <p className="xo-upload-compose-note" role="status"><CircleAlert aria-hidden="true" /><span>{tx("ui.uploads.value_will_supersede_the_existing_result_6dcb7bb6", { value: repeated.map((done) => done.display_name).join(', ') })}</span></p>
                )}
                {missingFactsNotice && <p className="xo-upload-inline-error" role="alert">{missingFactsNotice}</p>}
                <footer className="xo-upload-journey-actions">
                  <button type="button" className="is-secondary" disabled={busy} onClick={() => setJourneyStep('identity')}>{tx("ui.uploads.back_b52b36b7")}</button>
                  <button type="button" className="xo-btn" disabled={!configurationReady || busy} onClick={() => setJourneyStep('files')}>{tx("ui.uploads.continue_2e026239")}<ChevronRight aria-hidden="true" /></button>
                </footer>
              </>
            )}

            {journeyStep === 'files' && (
              <>
                <header className="xo-upload-journey-stage-head"><h2>{tx("ui.uploads.drop_the_source_files_95c2cd56")}</h2></header>
                <div className="xo-upload-journey-summary">
                  <span>{sourceMode === 'collection' ? <Package aria-hidden="true" /> : <UserRound aria-hidden="true" />}</span>
                  <div><strong>{sourceMode === 'collection' ? resolvedCollection?.code : patient.patient?.name}</strong><small>{tx("ui.uploads.value_visit_visit_eeddf298", { value: sampleTypes.find((type) => type.code === sampleCode)?.name || sampleCode, visit: visit })}</small></div>
                  <em>{selectedPipelines.length === 1
                    ? tx('ui.uploads.one_pipeline', { count: selectedPipelines.length })
                    : tx('ui.uploads.many_pipelines', { count: selectedPipelines.length })}</em>
                </div>
                <p className="xo-upload-files-prompt">
                  {pickedFiles.length > 0
                    ? pickedFiles.length === 1
                      ? tx('ui.uploads.one_file_with_size', { count: pickedFiles.length, size: bytes(pickedBytes) })
                      : tx('ui.uploads.many_files_with_size', { count: pickedFiles.length, size: bytes(pickedBytes) })
                    : tx("ui.uploads.add_the_required_files_a1320d66")}
                </p>
                <div className="xo-upload-file-list xo-upload-journey-files">
                  {slots.map((slot) => {
                    const selectedFiles = hasFiles(chosen[slot.key])
                      ? (Array.isArray(chosen[slot.key]) ? chosen[slot.key] : [chosen[slot.key]]) : [];
                    return (
                      <Field key={slot.key} id={`slot-${slot.key}`}
                        label={slot.is_required ? slot.title : tx('ui.uploads.optional_slot', { title: slot.title })}
                        hint={tx("ui.uploads.content_kind_label_file_format_value_cb2e9cd8", { content_kind_label: slot.content_kind_label, file_format: slot.file_format, value: slot.neededBy.join(', ') })}>
                        <div className={`xo-upload-file-picker${selectedFiles.length > 0 ? ' has-files' : ''}`}>
                          <input {...fieldAria(`slot-${slot.key}`, { hint: 'x' })} type="file"
                            multiple={slot.accepts_multiple} disabled={busy}
                            onChange={(event) => setChosen((current) => ({
                              ...current,
                              [slot.key]: slot.accepts_multiple ? Array.from(event.target.files) : event.target.files[0],
                            }))} />
                          <span><FileUp aria-hidden="true" /></span>
                          <div><strong>{selectedFiles.length > 0
                            ? selectedFiles.length === 1
                              ? tx('ui.uploads.one_file_selected', { count: selectedFiles.length })
                              : tx('ui.uploads.many_files_selected', { count: selectedFiles.length })
                            : slot.accepts_multiple
                              ? tx('ui.uploads.choose_files_of_format', { format: slot.file_format })
                              : tx('ui.uploads.choose_file_of_format', { format: slot.file_format })}</strong>
                            <small>{slot.accepts_multiple ? tx("ui.uploads.multiple_files_accepted_f71f3220") : tx("ui.uploads.one_file_required_c397cceb")}</small></div>
                          <em>{tx("ui.uploads.browse_2f3b5c55")}</em>
                        </div>
                        {selectedFiles.length > 0 && (
                          <ul className="xo-upload-picked-files">
                            {selectedFiles.map((file, index) => {
                              const fraction = progress[`${slot.key}#${index + 1}`];
                              return <li key={`${file.name}-${file.size}-${index}`}><FileCheck2 aria-hidden="true" />
                                <span><strong>{file.name}</strong><small>{bytes(file.size)}</small></span>
                                {fraction !== undefined && <em>{Math.round(fraction * 100)}%</em>}</li>;
                            })}
                          </ul>
                        )}
                      </Field>
                    );
                  })}
                </div>
                {repeated.length > 0 && sourceMode === 'collection' && (
                  <p className="xo-upload-compose-note" role="status"><CircleAlert aria-hidden="true" /><span>{tx("ui.uploads.value_will_supersede_the_existing_result_6dcb7bb6", { value: repeated.map((done) => done.display_name).join(', ') })}</span></p>
                )}
                {error && <p className="xomics-auth-error" role="alert">{error}</p>}
                {busy && <div className="xo-upload-transfer" role="progressbar" aria-label={submitLabel}
                  aria-valuemin="0" aria-valuemax="100" aria-valuenow={Math.round(transferPercent)}><span style={{ width: `${transferPercent}%` }} /></div>}
                <footer className="xo-upload-journey-actions">
                  <button type="button" className="is-secondary" disabled={busy}
                    onClick={() => setJourneyStep(sourceMode === 'collection' ? 'identity' : 'configure')}>{tx("ui.uploads.back_b52b36b7")}</button>
                  <button type="submit" className="xo-btn" disabled={!ready || !filesReady || busy}>
                    {busy && <Loader2 className="xo-spin" aria-hidden="true" />}{submitLabel}
                  </button>
                </footer>
              </>
            )}
          </section>
        )}
      </form>
    </section>
  );
}

// ---------------------------------------------------------------- detail

function UploadDetail({ uuid, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const load = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      setDetail(await loadXomicsUpload(uuid));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => { load(true); }, [load]);
  useLiveTopics(['uploads', 'jobs'], () => load(false));
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const upload = detail?.upload;
  const files = Array.isArray(detail?.files) ? detail.files : [];
  const jobs = Array.isArray(detail?.jobs) ? detail.jobs : [];
  const creator = upload && [upload.creator_first_name, upload.creator_last_name]
    .filter(Boolean).join(' ');
  const patientParams = readableJson(upload?.patient_params);
  const clock = useOperationalClock(jobs.some(
    (job) => ['pending', 'running', 'offloading'].includes(job.state),
  ));

  return (
    <div className="xo-upload-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="xo-upload-modal" role="dialog" aria-modal="true"
        aria-labelledby="upload-detail-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div className="xo-upload-modal-title">
            <span aria-hidden="true"><FileUp /></span>
            <div>
              <small>{tx("ui.uploads.upload_value_32b8f76a", { value: uuid.slice(0, 8) })}</small>
              <h2 id="upload-detail-title">{upload?.patient_name || upload?.original_name || tx("ui.uploads.analysis_details_035201de")}</h2>
              <p>{upload
                ? tx("ui.uploads.value_visit_value2_1fc70677", { value: upload.sample_name || upload.sample_code || tx('ui.uploads.sample_58fabfa7'), value2: upload.visit_number ?? '—' })
                : tx("ui.uploads.reading_the_upload_receipt_851f5145")}</p>
            </div>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.uploads.close_upload_details_48c19524")} onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>
        <nav className="xo-upload-modal-tabs" aria-label={tx("ui.uploads.upload_details_27bcadf0")}>
          {[
            ['overview', tx('ui.uploads.overview')],
            ['files', files.length > 0
              ? tx('ui.uploads.source_files_with_count', { count: files.length }) : tx('ui.uploads.source_files_e660d20b')],
            ['technical', tx('ui.uploads.technical_receipt')],
          ].map(([key, label]) => (
            <button key={key} type="button" className={tab === key ? 'is-active' : ''}
              aria-pressed={tab === key} onClick={() => setTab(key)}>{label}</button>
          ))}
        </nav>
        <div className="xo-upload-modal-scroll">
          {error && <p className="xomics-auth-error" role="alert">{error}</p>}
          {!detail && loading && <p className="xomics-muted">{tx("ui.uploads.loading_upload_details_da801ca0")}</p>}
          {detail && tab === 'overview' && (
            <div className="xo-upload-modal-overview">
              <div className="xo-upload-modal-status">
                <div>
                  <span>{tx("ui.uploads.submitted_value_f09baa1c", { value: when(upload.created_at) })}</span>
                  <CollectionLink collection={upload.collection} />
                </div>
                <StateChip status={upload.status} />
              </div>
              <dl className="xo-upload-overview-facts">
                <DetailFact label={tx("ui.uploads.patient_de5c57ff")} value={upload.is_external
                  ? tx('ui.uploads.external_upload')
                  : (upload.patient_name || (upload.patient_id ? tx('ui.uploads.patient_number', { id: upload.patient_id }) : '—'))} />
                <DetailFact label={tx("ui.uploads.project_f6f4da8d")} value={[upload.parent_project_name, upload.subproject_code].filter(Boolean).join(' · ') || '—'} />
                <DetailFact label={tx("ui.uploads.specimen_465e8e7b")} value={tx('ui.uploads.sample_visit', {
                  sample: upload.sample_name || upload.sample_code || '—', visit: upload.visit_number ?? '—',
                })} />
                <DetailFact label={tx("ui.uploads.submitted_2e00359b")} value={when(upload.created_at)} />
                <DetailFact label={tx("ui.uploads.source_files_e660d20b")} value={`${files.length} · ${bytes(upload.total_bytes)}`} />
                <DetailFact label={tx("ui.uploads.submitted_by_046485aa")} value={creator || upload.creator_email || '—'} />
              </dl>
              {upload.error_message && (
                <div className="xo-alert t-bad"><CircleAlert aria-hidden="true" /><span><strong>{tx("ui.uploads.upload_error_ccc9d0ad")}</strong>{upload.error_message}</span></div>
              )}
              <section className="xo-upload-modal-section">
                <header><div><h3>{tx("ui.uploads.analyses_f864c045")}</h3><p>{tx("ui.uploads.live_queue_runtime_and_result_offload_state_5794fa49")}</p></div><span>{jobs.length}</span></header>
                {jobs.length === 0 ? <p className="xo-muted">{tx("ui.uploads.no_analyses_were_created_from_this_upload_82534459")}</p> : (
                  <div className="xo-upload-overview-jobs">
                    {jobs.map((job) => <JobProgress key={job.uuid} job={job} clock={clock} />)}
                  </div>
                )}
              </section>
            </div>
          )}

          {detail && tab === 'files' && (
            <section className="xo-upload-modal-section is-files">
              <header><div><h3>{tx("ui.uploads.source_files_e660d20b")}</h3><p>{tx("ui.uploads.the_verified_inputs_mapped_into_this_upload_4bcdb1a1")}</p></div><span>{files.length}</span></header>
              {files.length === 0 ? <p className="xo-muted">{tx("ui.uploads.no_source_files_were_recorded_dc2328c1")}</p> : (
                <div className="xo-upload-modal-files">
                  {files.map((file) => (
                    <article key={file.position}>
                      <span aria-hidden="true"><FileCheck2 /></span>
                      <div><strong>{file.original_name}</strong><small>{joinedJson(file.fills)}</small></div>
                      <dl><div><dt>{tx("ui.uploads.size_b7152342")}</dt><dd>{bytes(file.actual_bytes || file.declared_bytes)}</dd></div>
                        <div><dt>{tx("ui.uploads.verified_aed3b8c6")}</dt><dd>{when(file.verified_at)}</dd></div></dl>
                      <StateChip status={file.verify_status} />
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}

          {detail && tab === 'technical' && (
            <div className="xo-upload-technical-receipt">
              <section className="xo-upload-technical-section">
                <header><div><h3>{tx("ui.uploads.upload_record_baa0b865")}</h3><p>{tx("ui.uploads.database_links_and_lifecycle_timestamps_f62baf8c")}</p></div></header>
                <dl className="xo-upload-facts is-upload-record">
                  <DetailFact label={tx("ui.uploads.patient_row_c2275a87")} value={upload.patient_id ?? '—'} />
                  <DetailFact label={tx("ui.uploads.parent_project_row_61da9065")} value={upload.parent_project_id ?? '—'} />
                  <DetailFact label={tx("ui.uploads.subproject_row_e8d72795")} value={upload.sub_project_id ?? '—'} />
                  <DetailFact label={tx("ui.uploads.created_accf40c8")} value={when(upload.created_at)} />
                  <DetailFact label={tx("ui.uploads.verified_completed_a0ad3829")} value={when(upload.completed_at)} />
                  <DetailFact label={tx("ui.uploads.last_update_cf1b9167")} value={when(upload.updated_at)} />
                  <DetailFact label={tx("ui.uploads.signed_urls_expire_4cd469a2")} value={when(upload.urls_expire_at)} />
                  <DetailFact label={tx("ui.uploads.upload_uuid_4560853d")} value={upload.uuid} mono />
                </dl>
                {patientParams && <details className="xo-upload-technical"><summary>{tx("ui.uploads.patient_parameters_7d3c12ed")}</summary><pre>{patientParams}</pre></details>}
              </section>

              <section className="xo-upload-technical-section">
                <header><div><h3>{tx("ui.uploads.storage_receipt_8c0a444d")}</h3><p>{tx("ui.uploads.verified_source_objects_held_for_this_upload_f7eb32ec")}</p></div><span>{files.length}</span></header>
                <dl className="xo-upload-storage-facts">
                  <DetailFact label={tx("ui.uploads.r2_bucket_422e3992")} value={upload.r2_bucket || '—'} mono />
                  <DetailFact label={tx("ui.uploads.upload_prefix_ff372287")} value={upload.object_prefix || '—'} mono />
                </dl>
                <details className="xo-upload-technical">
                  <summary>{tx("ui.uploads.source_object_details_b6a95927")}</summary>
                  <div className="xomics-table-scroll"><table className="xomics-table"><thead><tr>
                    <th>#</th><th>{tx("ui.uploads.file_2c3cafa4")}</th><th>{tx("ui.uploads.declared_8df7435b")}</th><th>{tx("ui.uploads.actual_7bb2ee60")}</th><th>{tx("ui.uploads.part_size_6c0f28d2")}</th><th>{tx("ui.uploads.r2_object_3c22d0ee")}</th><th>{tx("ui.uploads.etag_11d00f6e")}</th>
                  </tr></thead><tbody>{files.map((file) => <tr key={file.position}>
                    <td>{file.position}</td><td>{file.original_name}</td><td>{bytes(file.declared_bytes)}</td>
                    <td>{bytes(file.actual_bytes)}</td><td>{bytes(file.part_size_bytes)}</td>
                    <td className="xomics-mono xomics-wrap">{file.object_key}</td><td className="xomics-mono xomics-wrap">{file.etag || '—'}</td>
                  </tr>)}</tbody></table></div>
                </details>
              </section>

              <section className="xo-upload-technical-section">
                <header><div><h3>{tx("ui.uploads.pipeline_runs_af95a171")}</h3><p>{tx("ui.uploads.execution_inputs_outputs_and_worker_receipts_a2e8bdc1")}</p></div><span>{jobs.length}</span></header>
                {jobs.length === 0 ? <p className="xo-muted">{tx("ui.uploads.no_pipeline_jobs_were_created_8c3bee07")}</p> : (
                  <div className="xo-upload-jobs">
                    {jobs.map((job) => <JobDetail key={job.uuid} job={job} clock={clock} />)}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- list

// The customers' samples waiting for their data (Uploads.js, GET /requested), as a
// popup of the page's Requested uploads button: choosing one fills in its upload.
// The dashboard's widget and the laboratory's notice of each lead to the same.
function RequestedUploadsDialog({ requested, error, onClose, onUpload }) {
  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);
  return (
    <div className="xo-upload-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="xo-upload-modal xo-upload-requested-modal" role="dialog" aria-modal="true"
        aria-labelledby="upload-requested-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div className="xo-upload-modal-title">
            <span aria-hidden="true"><Inbox /></span>
            <div>
              <h2 id="upload-requested-title">{tx('ui.uploads.requested_title')}</h2>
              <p>{tx('ui.uploads.requested_detail')}</p>
            </div>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx('ui.uploads.requested_close')} onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="xo-upload-modal-scroll">
          {error && <p className="xomics-auth-error" role="alert">{error}</p>}
          {!error && requested.length === 0 && <p className="xomics-muted">{tx('ui.uploads.requested_empty')}</p>}
          {requested.length > 0 && (
            <ul className="xo-upload-requested-list">
              {requested.map((item) => (
                <li key={item.code}>
                  <button type="button" onClick={() => onUpload(item.code)}>
                    <span>
                      <strong>{item.person || item.sampleName || item.code}</strong>
                      <small>{[item.code, item.person && item.sampleName ? item.sampleName : null].filter(Boolean).join(' · ')}</small>
                      <small>{tx('ui.dash.requested.where', { customer: item.customer, project: item.project, submission: item.submission })}</small>
                    </span>
                    <span className="xo-upload-requested-go"><FileUp aria-hidden="true" />{tx('ui.uploads.requested_upload')}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

export default function XomicsUploads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = useXomicsAuth();
  const mayUpload = permissions.includes('UPLOAD_CREATE');
  const [requested, setRequested] = useState([]);
  const [requestedError, setRequestedError] = useState('');
  const [requestedOpen, setRequestedOpen] = useState(false);
  const [catalogue, setCatalogue] = useState({ release: null, sampleTypes: [], pipelines: [] });
  const [catalogueReady, setCatalogueReady] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openUuid, setOpenUuid] = useState(null);
  const [railTab, setRailTab] = useState('uploads');
  const [jobFilter, setJobFilter] = useState('all');
  const [jobs, setJobs] = useState([]);
  const [jobCounts, setJobCounts] = useState(EMPTY_JOB_COUNTS);
  const [jobTotal, setJobTotal] = useState(0);
  const [jobOffset, setJobOffset] = useState(0);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState('');
  const jobsGeneration = useRef(0);
  const [railQuery, setRailQuery] = useState('');
  const linkedUpload = useMemo(() => {
    const collectionId = positiveId(searchParams.get('collectionId'));
    const patientId = positiveId(searchParams.get('patientId'));
    const sampleUuid = String(searchParams.get('sampleUuid') ?? '').trim();
    if (collectionId === null || patientId === null || !UUID_RE.test(sampleUuid)) return null;
    return {
      key: `${sampleUuid}:${collectionId}:${patientId}`,
      sampleUuid,
      collectionId,
      patientId,
    };
  }, [searchParams]);
  const clearLinkedUpload = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    ['collectionId', 'patientId', 'sampleUuid'].forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  // ?code= (the dashboard's Requested uploads, or the laboratory's notice of
  // one) fills in the upload once, then leaves the address, so the same code
  // can be handed over again.
  const requestedCode = collectionLookupCode(String(searchParams.get('code') ?? ''));
  const clearRequestedCode = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('code');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const loadRequested = useCallback(() => {
    if (!mayUpload) return;
    loadRequestedUploads()
      .then((rows) => { setRequested(rows); setRequestedError(''); })
      .catch((requestError) => setRequestedError(errorMessage(requestError)));
  }, [mayUpload]);
  useEffect(() => { loadRequested(); }, [loadRequested]);
  useLiveTopics(['uploads', 'notifications'], loadRequested);
  const uploadRequested = useCallback((code) => {
    setRequestedOpen(false);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('code', code);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const fetchPage = useCallback(async (nextOffset, showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const result = await loadXomicsUploads({ limit: PAGE_SIZE, offset: nextOffset });
      setUploads(result.uploads);
      setTotal(result.total);
      setOffset(nextOffset);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);
  const fetchJobs = useCallback(async (
    nextOffset, nextFilter = 'all', nextQuery = '', showLoading = true,
  ) => {
    const generation = jobsGeneration.current + 1;
    jobsGeneration.current = generation;
    if (showLoading) setJobsLoading(true);
    setJobsError('');
    try {
      const result = await loadXomicsJobs({
        status: nextFilter,
        query: nextQuery,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      if (generation !== jobsGeneration.current) return;
      setJobs(result.jobs);
      setJobCounts(result.counts);
      setJobTotal(result.total);
      setJobOffset(nextOffset);
    } catch (requestError) {
      if (generation === jobsGeneration.current) setJobsError(errorMessage(requestError));
    } finally {
      if (generation === jobsGeneration.current) setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUploadCatalogue()
      .then(setCatalogue)
      .catch(() => {})
      .finally(() => setCatalogueReady(true));
    fetchPage(0);
    fetchJobs(0, 'all', '', false);
  }, [fetchJobs, fetchPage]);
  useEffect(() => {
    if (railTab !== 'jobs') return undefined;
    const timer = window.setTimeout(
      () => fetchJobs(0, jobFilter, railQuery, true),
      railQuery.trim() === '' ? 0 : 250,
    );
    return () => window.clearTimeout(timer);
  }, [fetchJobs, jobFilter, railQuery, railTab]);
  useLiveTopics(['uploads', 'jobs'], () => {
    fetchPage(offset, false);
    fetchJobs(jobOffset, jobFilter, railTab === 'jobs' ? railQuery : '', false);
  });

  const activeClock = uploads.some((upload) => uploadLane(upload) === 'active')
    || jobs.some(({ job }) => ['pending', 'running', 'offloading'].includes(job?.state));
  const clock = useOperationalClock(activeClock);
  const railUploads = useMemo(() => {
    const needle = railQuery.trim().toLowerCase();
    return uploads.filter((upload) => {
      if (needle === '') return true;
      const jobs = Array.isArray(upload.job_summaries) ? upload.job_summaries : [];
      return [
        upload.uuid, upload.patient_name, upload.patient_id,
        upload.parent_project_name, upload.subproject_code,
        upload.sample_name, upload.sample_code, upload.pipeline_versions,
        upload.collection?.code, upload.collection?.printedCode,
        ...jobs.flatMap((job) => [job.display_name, job.pipeline, job.omics, job.code_version]),
      ].filter((value) => value !== null && value !== undefined)
        .join(' ').toLowerCase().includes(needle);
    }).sort((left, right) => Number(right.created_at || 0) - Number(left.created_at || 0));
  }, [railQuery, uploads]);

  return (
    <>
      <div className="xo-upload-board">
        <main className="xo-uploads-main">
          <Head title={tx("ui.uploads.uploads_227cc640")} subtitle={tx("ui.uploads.prepare_samples_and_monitor_analyses_79aabc50")}>
            {mayUpload && (
              <button type="button" className={`xo-btn-2 xo-upload-requested-button${requested.length > 0 ? ' has-requests' : ''}`}
                onClick={() => setRequestedOpen(true)}>
                <Inbox aria-hidden="true" />{tx('ui.uploads.requested_title')}<b>{requested.length}</b>
              </button>
            )}
          </Head>
          {requestedOpen && (
            <RequestedUploadsDialog requested={requested} error={requestedError}
              onClose={() => setRequestedOpen(false)} onUpload={uploadRequested} />
          )}

          <section className="xo-upload-journey-shell">
            <NewUpload catalogue={catalogue} catalogueReady={catalogueReady}
              linkedUpload={linkedUpload} onClearLinkedUpload={clearLinkedUpload}
              requestedCode={requestedCode} onRequestedCodeUsed={clearRequestedCode}
              onDone={() => fetchPage(0, false)} />
          </section>
        </main>

        <aside className="xo-upload-run-rail">
          <RailHeader title={tx("ui.uploads.upload_activity_bfbeb091")} />
          <WorkspaceTabs className="xo-upload-rail-tabs is-rail" value={railTab}
            label={tx("ui.uploads.upload_activity_bfbeb091")}
            onChange={(nextTab) => { setRailTab(nextTab); setRailQuery(''); }}
            tabs={[
              { value: 'uploads', label: tx("ui.uploads.uploads_227cc640"), count: total, Icon: FileUp },
              { value: 'jobs', label: tx("ui.uploads.jobs_437736fd"), count: jobCounts.all, Icon: Activity },
            ]} />
          <div className="xo-workspace-search xo-upload-run-rail-search">
            <SearchField value={railQuery}
             placeholder={railTab === 'jobs' ? tx("ui.uploads.search_jobs_6bb8f735") : tx("ui.uploads.search_uploads_c1f07e61")}
             label={railTab === 'jobs' ? tx("ui.uploads.search_jobs_e5d32343") : tx("ui.uploads.search_uploads_c0effbf6")}
             onChange={setRailQuery} />
          </div>
          {railTab === 'jobs' && (
            <nav className="xo-upload-job-filters" aria-label={tx("ui.uploads.filter_jobs_by_status_3f05acb9")}>
              {[
                ['all', tx('ui.uploads.filter_all')],
                ['queued', tx('ui.uploads.filter_queued')],
                ['running', tx('ui.uploads.filter_running')],
                ['completed', tx('ui.uploads.filter_completed')],
                ['failed', tx('ui.uploads.filter_failed')],
              ].map(([value, label]) => (
                <button key={value} type="button" aria-pressed={jobFilter === value}
                  className={jobFilter === value ? 'is-active' : ''}
                  onClick={() => setJobFilter(value)}>
                  <span>{label}</span><em>{jobCounts[value]}</em>
                </button>
              ))}
            </nav>
          )}
          {(railTab === 'jobs' ? jobsError : error)
            && <p className="xo-upload-rail-error" role="alert">{railTab === 'jobs' ? jobsError : error}</p>}
          <div className="xo-upload-rail-list">
            {railTab === 'jobs' ? (
              jobsLoading && jobs.length === 0 ? (
                <div className="xo-upload-board-state"><Loader2 className="xo-spin" aria-hidden="true" /><strong>{tx("ui.uploads.loading_33ce4174")}</strong></div>
              ) : jobs.length === 0 ? (
                <div className="xo-upload-board-state"><CheckCircle2 aria-hidden="true" /><strong>{tx("ui.uploads.no_816c52fd")} {jobFilter === 'all' ? '' : tx("ui.uploads.jobfilter_d4772166", { jobFilter: jobFilter })}{tx("ui.uploads.jobs_69669221")}</strong></div>
              ) : jobs.map(({ upload, job }) => (
                <RunRailItem key={job.uuid} upload={upload} job={job} clock={clock}
                  onOpen={() => setOpenUuid(upload.uuid)} />
              ))
            ) : loading && uploads.length === 0 ? (
              <div className="xo-upload-board-state"><Loader2 className="xo-spin" aria-hidden="true" /><strong>{tx("ui.uploads.loading_33ce4174")}</strong></div>
            ) : railUploads.length === 0 ? (
              <div className="xo-upload-board-state"><FlaskConical aria-hidden="true" /><strong>{tx("ui.uploads.no_uploads_found_b4706f5d")}</strong></div>
            ) : railUploads.map((upload) => (
              <UploadRailItem key={upload.uuid} upload={upload} clock={clock}
                onOpen={() => setOpenUuid(upload.uuid)} />
            ))}
          </div>
          <footer>
            {railTab === 'jobs' ? (
              <><span>{tx("ui.uploads.all_jobs_on_this_page_f5de24a3", { all: jobs.length })}</span><Pager offset={jobOffset} count={jobs.length} total={jobTotal}
                pageSize={PAGE_SIZE} busy={jobsLoading}
                onChange={(nextOffset) => fetchJobs(nextOffset, jobFilter, railQuery)} /></>
            ) : (
              <><span>{tx("ui.uploads.total_uploads_18a65fc0", { total: total })}</span><Pager offset={offset} count={uploads.length} total={total}
                pageSize={PAGE_SIZE} busy={loading} onChange={fetchPage} /></>
            )}
          </footer>
        </aside>
      </div>

      {openUuid && <UploadDetail uuid={openUuid} onClose={() => setOpenUuid(null)} />}
    </>
  );
}
